/**
 * Phase K Pass 2 — Minimal admin contracts list page.
 *
 * Backs the `pending-signatures` dashboard tile's click-through. Filters
 * by contract `status` (default = sent_for_signature).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, ArrowUpRight } from "lucide-react";

const STATUS_OPTIONS = [
    { key: "sent_for_signature", label: "Sent · awaiting signature" },
    { key: "customer_signed", label: "Customer signed" },
    { key: "fully_signed", label: "Fully signed" },
    { key: "draft", label: "Drafts" },
    { key: "cancelled", label: "Cancelled" },
];

function fmtRel(iso) {
    if (!iso) return "—";
    try {
        const diff = Date.now() - new Date(iso).getTime();
        const day = Math.floor(diff / 86400000);
        if (day < 1) return "today";
        if (day === 1) return "1 day ago";
        if (day < 30) return `${day} days ago`;
        const mon = Math.floor(day / 30);
        return `${mon}mo ago`;
    } catch {
        return iso;
    }
}

function StatusPill({ status }) {
    const tones = {
        draft: "bg-slate-100 text-slate-700 ring-slate-200",
        sent_for_signature: "bg-amber-50 text-amber-900 ring-amber-200",
        customer_signed: "bg-blue-50 text-blue-900 ring-blue-200",
        fully_signed: "bg-emerald-50 text-emerald-900 ring-emerald-200",
        cancelled: "bg-red-50 text-red-700 ring-red-200",
    };
    const cls = tones[status] || tones.draft;
    return (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${cls}`}>
            {status?.replace(/_/g, " ")}
        </span>
    );
}

export default function AdminContractsListPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const status = params.get("status") || "sent_for_signature";

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/admin/contracts", {
                    params: { status, limit: 100 },
                });
                setItems(data.items || []);
                setTotal(data.total || 0);
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
    }, [status]);

    const setStatus = (next) => {
        const q = new URLSearchParams(location.search);
        q.set("status", next);
        navigate({ pathname: location.pathname, search: `?${q.toString()}` });
    };

    return (
        <DashboardShell>
            <div data-testid="admin-contracts-list-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-0 py-6 space-y-5">
                <header className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">All customers</p>
                        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Contracts</h1>
                        <p className="text-sm text-slate-500 mt-1">
                            {loading ? "Loading…" : `${total} contract${total === 1 ? "" : "s"} match this filter.`}
                        </p>
                    </div>
                </header>

                <div className="flex flex-wrap items-center gap-2">
                    {STATUS_OPTIONS.map((opt) => (
                        <Button
                            key={opt.key}
                            type="button"
                            variant={opt.key === status ? "default" : "outline"}
                            size="sm"
                            onClick={() => setStatus(opt.key)}
                            data-testid={`contracts-filter-${opt.key}`}
                            className={opt.key === status ? "bg-blue-900 hover:bg-blue-800" : ""}
                        >
                            {opt.label}
                        </Button>
                    ))}
                </div>

                {loading && (
                    <p className="text-sm text-slate-500">Loading contracts…</p>
                )}

                {!loading && items.length === 0 && (
                    <Card data-testid="contracts-empty">
                        <CardContent className="py-12 text-center text-sm text-slate-600">
                            No contracts match this filter.
                        </CardContent>
                    </Card>
                )}

                {!loading && items.length > 0 && (
                    <Card>
                        <CardContent className="p-0 divide-y divide-slate-100">
                            {items.map((c) => (
                                <Link
                                    key={c.id}
                                    to={`/admin/contracts/${c.id}`}
                                    data-testid={`contract-row-${c.id}`}
                                    className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                                >
                                    <div className="min-w-0 flex items-center gap-3">
                                        <FileText size={16} className="text-slate-400 shrink-0" />
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium text-slate-900 truncate">
                                                {c.title || "Untitled contract"}
                                            </p>
                                            <p className="text-xs text-slate-500 truncate">
                                                {c.customer_company_name || "—"} · sent {fmtRel(c.sent_at || c.created_at)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0">
                                        <StatusPill status={c.status} />
                                        <ArrowUpRight size={14} className="text-slate-400" />
                                    </div>
                                </Link>
                            ))}
                        </CardContent>
                    </Card>
                )}
            </div>
        </DashboardShell>
    );
}
